import React, { useEffect, useState } from "react";
import { useContext } from "react";
import TherapyRoomCard from "./TherapyRoomCard";
import RefreshMeetingsButton from "./RefreshMeetingsButton";
import useMeetingsRefresh from "../../hooks/useMeetingsRefresh";
import Navbar from "../Navbar";
import Footer from "../Footer";
import CDAModal from "../CDAModal";
import { AuthContext } from "../../context/AuthContext";
import beach from "../../assets/images/beach.png";
import forest from "../../assets/images/forest.png";
import campsite from "../../assets/images/campsite.png";
import park from "../../assets/images/park.png";
import winterHoliday from "../../assets/images/winterHoliday.png"
import zen from "../../assets/images/zen.png"
import aiAssistantModel from "../../assets/images/ai-assistant-model.png";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Env from "../../runtimeEnv";
import axios from "axios";
import WarningBanner from "../WarningBanner";
import SessionFeedbackModal from "../SessionFeedbackModal";
import { isNativeMobileShell } from "../../native/mobileBridge";
import {
  isUnityRoomCached,
  markUnityRoomPreloaded,
  preloadUnityRoom,
  prioritizeUnityRooms,
} from "../../utils/unityWebglPreloader";

const CLIENT_PRELOAD_ROOMS = [
  { id: "beach" },
  { id: "mountain" },
  { id: "park" },
  { id: "campsite" },
  { id: "winter-holiday" },
];


function ClientTherapyRooms() {
  const navigate = useNavigate();
  const location = useLocation();
  const [enabledRoom, setEnabledRoom] = useState([]);
  const [roomsOnlineStatus, setRoomsOnlineStatus] = useState({});
  const [sessionStartTimes, setSessionStartTimes] = useState({});
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [meetingExists, setMeetingExists] = useState(false);
  const [showCDAModal, setShowCDAModal] = useState(false);
  const [isAILocked, setIsAILocked] = useState(false);
  const [aiLockLoading, setAiLockLoading] = useState(false);
  const [showSessionFeedback, setShowSessionFeedback] = useState(false);
  const [feedbackEligibilityChecked, setFeedbackEligibilityChecked] = useState(false);
  const [selectedSpaceLoaded, setSelectedSpaceLoaded] = useState(false);
  const [preloadStates, setPreloadStates] = useState({});


  const {
    currentNickname,
    setCurrentNickname,
    PlutonicuserID,
    loading,
    setLoading,
    selectedTherapyType,
    setSelectedTherapyType,
    userType,
    verifiedUserAccess,
  } = useContext(AuthContext);

  const isAIMH = location.pathname === "/aimh";
  const currentTherapyType = isAIMH ? "ai" : selectedTherapyType || "human";

  useEffect(() => {
    if (
      loading ||
      feedbackEligibilityChecked ||
      currentTherapyType !== "human" ||
      !["Client", "client-ai-enabled-therapy", "client-ai-companion"].includes(userType)
    ) return;

    setFeedbackEligibilityChecked(true);
    axios.post(
      `${Env.REACT_APP_PYTHON_API_URL}/api/feedback-prompt`,
      {},
      { withCredentials: true }
    ).then((response) => {
      if (response.data.show) setShowSessionFeedback(true);
    }).catch((error) => {
      console.warn("ClientTherapyRooms.js: warn event");
    });
  }, [currentTherapyType, feedbackEligibilityChecked, loading, userType]);

  const getFallbackAccess = (userType) => {
    switch (userType) {
      case "client-ai-enabled-therapy":
        return { canAccessAI: true, canAccessTherapy: true, isTherapist: false };
      case "client-ai-companion":
        return { canAccessAI: true, canAccessTherapy: false, isTherapist: false };
      case "Client":
        return { canAccessAI: false, canAccessTherapy: true, isTherapist: false };
      case "Therapist":
        return { canAccessAI: true, canAccessTherapy: true, isTherapist: true };
      default:
        return { canAccessAI: false, canAccessTherapy: false, isTherapist: false };
    }
  };

  const fallbackAccess = getFallbackAccess(userType);
  const userAccess = (verifiedUserAccess.canAccessAI !== undefined) ? verifiedUserAccess : fallbackAccess;
  
  useEffect(() => {
    if (isAIMH) {
      if (!userAccess.canAccessAI) {
        alert("You don't have access to AI companion rooms. Please upgrade your subscription.");
        navigate("/therapy-rooms");
        return;
      }
      setSelectedTherapyType("ai");
    } else if (location.pathname === "/therapy-rooms") {
      if (!userAccess.canAccessTherapy) {
        alert("You don't have access to therapy rooms. Please contact support.");
        navigate("/");
        return;
      }
      setSelectedTherapyType("human");
    }
  }, [location.pathname, setSelectedTherapyType, isAIMH, userAccess, navigate]);

  useEffect(() => {
    if (isAIMH) {
      const userId = PlutonicuserID || localStorage.getItem("PlutonicuserID");
      if (userId) {
        const cdaAgreedKey = `cda_agreed_${userId}`;
        const hasAgreedToCDA = localStorage.getItem(cdaAgreedKey);

        if (!hasAgreedToCDA) {
          setShowCDAModal(true);
        }
      }
    }
  }, [isAIMH, PlutonicuserID]);


useEffect(() => {
  if (isAIMH && PlutonicuserID) {
    setAiLockLoading(true);

    axiosInstance
      .get(`/api/get-locked-status/${PlutonicuserID}`)
      .then((res) => setIsAILocked(res.data.locked))
      .catch((err) => {
        console.error("ClientTherapyRooms.js: error event");
        setIsAILocked(true); // 🔐 fail-safe lock
      })
      .finally(() => setAiLockLoading(false));
  }
}, [isAIMH, PlutonicuserID]);


  const { refreshMeetings, refreshing, refreshError } = useMeetingsRefresh(async (signal) => {
      const savedUserID =
        PlutonicuserID || localStorage.getItem("PlutonicuserID");
      const savedNickname =
        currentNickname || localStorage.getItem("currentNickname");

      if (savedNickname) {
        setCurrentNickname(savedNickname);
      }

      if (savedUserID) {
        try {
          // For AI therapy, create or find AI session
          // if (selectedTherapyType === "ai") {
          //   await initializeAISession(savedUserID);
          // }

          const response = await axios.get(
            `${Env.REACT_APP_PYTHON_API_URL}/api/GetMeetings/${savedUserID}`,
            { withCredentials: true,  signal, params: { t: Date.now() } }
          );

          if (signal.aborted) return;

          if (response.data.length > 0) {
            const nowUTC = new Date();
            const gracePeriodBeforeMs = 15 * 60 * 1000;
            const gracePeriodAfterMs = 75 * 60 * 1000;
            const backToBackBufferMs = 20 * 60 * 1000;

            let hasEligibleMeeting = false;
            let latestUpcomingSession = null;

            const roomSessionsMap = response.data.reduce((acc, room) => {
              const roomId = room.meetingID.split(".")[0].replace("-therapy", "");
              const sessionTimeUTC = new Date(room.sessionTime);

              // Handle AI sessions differently
              const isAISession = room.meetingID.startsWith("beach-ai-");

              const formattedTimeEST = isAISession
                ? "Always Available"
                : sessionTimeUTC.toLocaleString("en-US", {
                    timeZone: "America/New_York",
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  }) + " EST";

              if (!acc[roomId]) acc[roomId] = [];

              acc[roomId].push({
                meetingID: room.meetingID,
                sessionStartTimeEST: formattedTimeEST,
                sessionStartTimeUTC: sessionTimeUTC.toISOString(),
                rawStartTime: sessionTimeUTC,
                online: room.online.some((s) => s === true),
                isAvailable: isAISession ? true : false, // AI sessions are always available
                isUpcoming: false,
                isAISession: isAISession,
              });

              return acc;
            }, {});

            Object.keys(roomSessionsMap).forEach((roomId) => {
              const sessions = roomSessionsMap[roomId];
              sessions.sort(
                (a, b) =>
                  new Date(a.sessionStartTimeUTC) -
                  new Date(b.sessionStartTimeUTC)
              );

              for (let i = 0; i < sessions.length; i++) {
                const current = sessions[i];
                const next = sessions[i + 1];

                // Skip time-based logic for AI sessions
                if (current.isAISession) {
                  current.isAvailable = true;
                  current.isUpcoming = false;
                  current.isPrimary = true;
                  hasEligibleMeeting = true;
                  continue;
                }

                const currentStart = new Date(current.sessionStartTimeUTC);
                const timeUntilCurrent = currentStart - nowUTC;
                const timeSinceCurrent = nowUTC - currentStart;

                const isInGraceWindow =
                  (timeUntilCurrent <= gracePeriodBeforeMs &&
                    timeUntilCurrent >= 0) ||
                  (timeSinceCurrent >= 0 &&
                    timeSinceCurrent <= gracePeriodAfterMs);

                const isUpcoming =
                  timeUntilCurrent > 0 &&
                  timeUntilCurrent <= gracePeriodBeforeMs;
                let isSuppressed = false;

                if (next) {
                  const nextStart = new Date(next.sessionStartTimeUTC);
                  const timeUntilNext = nextStart - nowUTC;
                  const allowSuppression =
                    timeUntilNext <= backToBackBufferMs &&
                    timeUntilNext >= -15 * 60 * 1000;

                  if (allowSuppression) {
                    isSuppressed = true;
                    next.isUpcoming = true;
                  }
                }

                current.isAvailable = isInGraceWindow;
                current.isUpcoming = isUpcoming;
                current.isPrimary =
                  (isInGraceWindow || isUpcoming) && !isSuppressed;

                // if (current.isAvailable || current.isUpcoming) {
                //   hasEligibleMeeting = true;
                // }
                hasEligibleMeeting = true; // Mark as true if *any* session exists, not just active/upcoming

                if (
                  current.isPrimary &&
                  (!latestUpcomingSession ||
                    currentStart >
                      new Date(latestUpcomingSession.sessionStartTimeUTC))
                ) {
                  latestUpcomingSession = current;
                }
              }
            });

            const sessionStartTimesMap = Object.entries(roomSessionsMap).reduce(
              (acc, [roomId, sessions]) => {
                const nextSession = sessions.find(
                  (session) => session.isAvailable || session.isUpcoming
                );
                if (nextSession) acc[roomId] = nextSession.sessionStartTimeEST;
                return acc;
              },
              {}
            );

            const onlineStatusMap = Object.entries(roomSessionsMap).reduce(
              (acc, [roomId, sessions]) => {
                acc[roomId] = sessions.some((session) => session.online);
                return acc;
              },
              {}
            );

            setRoomsOnlineStatus(onlineStatusMap);
            setEnabledRoom(roomSessionsMap);
            setSessionStartTimes(sessionStartTimesMap);
            setMeetingExists(hasEligibleMeeting);
          } else {
            setEnabledRoom({});
            setRoomsOnlineStatus({});
            setSessionStartTimes({});
            setMeetingExists(false);
          }
        } catch (error) {
          throw error;
        } finally {
          if (!signal.aborted) setLoading(false);
        }
      }

      if (!signal.aborted) setLoading(false);
  }, PlutonicuserID);

  useEffect(() => {
    if (PlutonicuserID) {
      axios
        .get(
          `${Env.REACT_APP_PYTHON_API_URL}/api/get-selected-space/${PlutonicuserID}`
        , { withCredentials: true })
        .then((res) => {
          const space = res.data.selectedSpace;
          if (space) {
            setSelectedSpace(space);
          } else {
          }
        })
        .catch((err) => console.error("ClientTherapyRooms.js: error event"))
        .finally(() => setSelectedSpaceLoaded(true));
    } else {
      setSelectedSpaceLoaded(true);
    }
  }, [PlutonicuserID, selectedTherapyType]);

  // const initializeAISession = async (userID) => {
  //   try {
  //     const response = await axios.get(
  //       `${Env.REACT_APP_PYTHON_API_URL}/api/GetMeetings/${userID}`
  //     );

  //     const existingAISession = response.data.find(
  //       (meeting) =>
  //         meeting.meetingID.startsWith("beach-ai-") &&
  //         meeting.meetingID.includes(userID)
  //     );

  //     if (!existingAISession) {
  //       // Create new AI session
  //       const aiMeetingID = `beach-ai-${userID}-${Date.now()}`;

  //       const sessionData = {
  //         meetingID: aiMeetingID,
  //         clientUserIDs: [userID],
  //         therapistUserID: "ai-companion",
  //         meetingName: "AI Therapy Session",
  //         sessionTime: new Date().toISOString(),
  //         isAISession: true,
  //         isPersistent: true,
  //         selectedSpace: "beach",
  //       };

  //       await axios.post(
  //         `${Env.REACT_APP_PYTHON_API_URL}/api/create-ai-session`,
  //         sessionData,
  //         { withCredentials: true }
  //       );
  //     }
  //   } catch (error) {
  //     console.error("Error initializing AI session:", error);
  //   }
  // };


  // ✅ Step 3: Handle space selection (only if meeting exists)
  const handleSpaceSelection = (spaceId) => {
    // console.log("Selected Space ID:", spaceId);
    // if (!meetingExists) {
    //     alert("No active meeting found. Cannot select a space.");
    //     return;
    // }
    if (!meetingExists) {
      // Find latest past session across all rooms
      const allSessions = Object.values(enabledRoom).flat();
      const now = new Date();
      const pastSessions = allSessions
        .filter((session) => new Date(session.rawStartTime) < now)
        .sort((a, b) => new Date(b.rawStartTime) - new Date(a.rawStartTime));

      const latestPastSession = pastSessions[0];

      if (!latestPastSession) {
        // alert("No session history found to save your selection.");
        return;
      }

      axios
        .post(`${Env.REACT_APP_PYTHON_API_URL}/api/select-space`, {
          PlutonicuserID: PlutonicuserID,
          selectedSpace: spaceId,
          meetingID: latestPastSession.meetingID, // Include the past meeting ID
        }, { withCredentials: true })
        .then(() => {
          setSelectedSpace(spaceId);
          // console.log("Space selection saved to latest past session.");
        })
        .catch((err) => console.error("ClientTherapyRooms.js: error event"));

      return;
    }

    if (selectedSpace === spaceId) {
      // console.log("Space is already selected. No API call needed.");
      return; // ✅ Avoid unnecessary API request
    }

    setSelectedSpace(spaceId);
    axios
      .post(`${Env.REACT_APP_PYTHON_API_URL}/api/select-space`, {
        PlutonicuserID: PlutonicuserID,
        selectedSpace: spaceId,
      }, { withCredentials: true })
      .then(() => void 0)
      .catch((err) => console.error("ClientTherapyRooms.js: error event"));
  };

  const createAICompanionMeeting = async (userID) => {
  try {
    const res = await axios.post(
      `${Env.REACT_APP_PYTHON_API_URL}/api/create-ai-companion-meetings`,
      { userID },
      { withCredentials: true }
    );
  } catch (err) {
    console.error("ClientTherapyRooms.js: error event");
  }
};



  const normalRooms = [
    {
      id: "zen-garden",
      title: "Plutonic Therapy Zen Garden",
      description:
        "Step into a calming Zen inspired retreat designed to hlep you relax, reflect, and feel more comfortable opening up with your therapist.",
      accessibility: "PC Web Browser | Chrome, Edge",
      style: "Seasonal",
      usage: "Seasonal",
      image: zen,
      available:
        enabledRoom["zen-garden"]?.some((session) => session.isAvailable) ||
        false,
      selectable: false, // Selectable if a meeting exists
      // enterable: enabledRoom["park"]?.some((session) => session.isAvailable) ?? false, // Enterable only if within grace period
      isComingSoon: true,
      online: false,
      sessionStartTimeEST: "",
    },
    {
      id: "beach",
      title: "Plutonic Therapy Beach Office",
      description:
        "Consider having a session with your licensed mental health professional inside the office or in the cabana. Take in the sounds of the ocean waves and the sunset as you reduce stress.",
      accessibility: "PC Web Browser | Chrome, Edge",
      style: "Modern Design",
      usage: "Family Counseling",
      image: beach,
      // available: enabledRoom["beach"]?.some((session) => session.isAvailable) || false,
      selectable: enabledRoom["beach"]?.length > 0 ?? false, // Selectable if a meeting exists
      available:
        enabledRoom["beach"]?.some((session) => session.isAvailable) ?? false, // Enterable only if within grace period
      isComingSoon: false,
      online: roomsOnlineStatus["beach"] || false,
      sessionStartTimeEST: sessionStartTimes["beach"] || "",
    },
    {
      id: "mountain",
      title: "Plutonic Therapy Forest Office",
      description:
        "Consider having a session with your licensed mental health professional next to the fireplace. Take in the sounds and sights of the forest.",
      accessibility: "PC Web Browser | Chrome, Edge",
      style: "Modern Design",
      usage: "Family Counseling",
      image: forest,
      // available: enabledRoom["mountain"]?.some((session) => session.isAvailable) || false,
      // selectable: false, // Cannot select "coming soon" rooms
      available:
        enabledRoom["mountain"]?.some((session) => session.isAvailable) ||
        false,
      selectable: enabledRoom["mountain"]?.length > 0 ?? false, // Selectable if a meeting exists
      isComingSoon: false,
      online: roomsOnlineStatus["mountain"] || false,
      sessionStartTimeEST: sessionStartTimes["mountain"] || "",
    },

    {
      id: "park",
      title: "Plutonic Therapy Quiet Park",
      description:
        "Consider having a session with your licensed mental health professional while taking a walk around the park or while sitting on a park bench. This quiet park is a perfect safe space to talk.",
      accessibility: "PC Web Browser | Chrome, Edge",
      style: "Green Space",
      usage: "Family Counseling",
      image: park,
      available:
        enabledRoom["park"]?.some((session) => session.isAvailable) || false,
      selectable: enabledRoom["park"]?.length > 0 ?? false, // Selectable if a meeting exists
      // enterable: enabledRoom["park"]?.some((session) => session.isAvailable) ?? false, // Enterable only if within grace period
      isComingSoon: false,
      online: roomsOnlineStatus["park"] || false,
      sessionStartTimeEST: sessionStartTimes["park"] || "",
    },
    {
      id: "campsite",
      title: "Plutonic Therapy Campfire",
      description:
        "Unpack your thoughts with your licensed mental health professional next to the campfire. Trees swaying and crickets chirping while enjoying the beautiful night sky. ",
      accessibility: "PC Web Browser | Chrome, Edge",
      style: "Green Space",
      usage: "Family Counseling",
      image: campsite,
      available:
        enabledRoom["campsite"]?.some((session) => session.isAvailable) ||
        false,
      selectable: enabledRoom["campsite"]?.length > 0 ?? false, // Selectable if a meeting exists
      // enterable: enabledRoom["park"]?.some((session) => session.isAvailable) ?? false, // Enterable only if within grace period
      isComingSoon: false,
      online: roomsOnlineStatus["campsite"] || false,
      sessionStartTimeEST: sessionStartTimes["campsite"] || "",
    },
    {
      id: "winter-holiday",
      title: "Plutonic Therapy Winter Holiday",
      description:
        "Step into a cozy winter lodge where soft snow falls outside and warm lantern lights fills the cabin. Settle into the gentle winter ambience.",
      accessibility: "PC Web Browser | Chrome, Edge",
      style: "Seasonal",
      usage: "Seasonal",
      image: winterHoliday,
      available:
        enabledRoom["winter-holiday"]?.some((session) => session.isAvailable) ||
        false,
      selectable: enabledRoom["winter-holiday"]?.length > 0 ?? false, // Selectable if a meeting exists
      // enterable: enabledRoom["park"]?.some((session) => session.isAvailable) ?? false, // Enterable only if within grace period
      isComingSoon: false,
      online: roomsOnlineStatus["winter-holiday"] || false,
      sessionStartTimeEST: sessionStartTimes["winter-holiday"] || "",
    },
  ];

  const aiRooms = [
    {
      id: "beach-ai-companion",
      title: "Plutonic Beach Office",
      description:
        "Talk to your mental health companion inside the office or in the cabana. Take in the sounds of the ocean waves and the sunset.",
      accessibility: "PC Web Browser | Chrome, Edge, Firefox",
      style: "Modern Design",
      usage: "Family Counseling",
      image: beach,
      selectable: !isAILocked, // disable selection when locked
    available: !isAILocked,  // disable enter when locked
      isComingSoon: false,
      online: true,
      sessionStartTimeEST: isAILocked ? "Restricted Access" : "Always Available",
    },
    // {
    //   id: "mountain-ai-companion",
    //   title: "Plutonic Therapy Forest Office",
    //   description:
    //     "Consider having a session with your licensed mental health professional next to the fireplace. Take in the sounds and sights of the forest.",
    //   accessibility: "PC Web Browser | Chrome, Edge",
    //   style: "Modern Design",
    //   usage: "Family Counseling",
    //   image: forest,
    //   available: false,
    //   selectable: false,
    //   isComingSoon: true,
    //   online: false,
    //   sessionStartTimeEST: "Unavailable",
    // },

    // {
    //   id: "park-ai-companion",
    //   title: "Plutonic Therapy Quiet Park",
    //   description:
    //     "Consider having a session with your licensed mental health professional while taking a walk around the park or while sitting on a park bench. This quiet park is a perfect safe space to talk.",
    //   accessibility: "PC Web Browser | Chrome, Edge",
    //   style: "Green Space",
    //   usage: "Family Counseling",
    //   image: park,
    //   available: false,
    //   selectable: false,
    //   isComingSoon: true,
    //   online: false,
    //   sessionStartTimeEST: "Unavailable",
    // },
    /* {
      id: "campsite-ai-companion",
      title: "Plutonic Campfire",
      description:
        "Unpack your thoughts with your mental health companion next to the campfire. Trees swaying and crickets chirping while enjoying the beautiful night sky.",
      accessibility: "PC Web Browser | Chrome, Edge, Firefox",
      style: "Green Space",
      usage: "Family Counseling",
      image: campsite,
      available: false,
      selectable: false,
      isComingSoon: true,
      online: false,
      sessionStartTimeEST: "Unavailable",
    }, */
  ];

  const getFilteredRooms = () => {
    if (currentTherapyType === "ai") {
      return userAccess.canAccessAI ? aiRooms : [];
    } else {
      return userAccess.canAccessTherapy ? normalRooms : [];
    }
  };

  const selectedRooms = getFilteredRooms();

  useEffect(() => {
    if (!selectedSpaceLoaded || loading || isNativeMobileShell() || currentTherapyType !== "human") {
      return undefined;
    }

    const controller = new AbortController();
    const roomsToPreload = prioritizeUnityRooms(CLIENT_PRELOAD_ROOMS, selectedSpace);

    const preloadInPriorityOrder = async () => {
      const cacheResults = await Promise.all(roomsToPreload.map(async (room) => [
        room.id,
        await isUnityRoomCached(room.id, controller.signal),
      ]));
      if (controller.signal.aborted) return;
      const cachedRoomIds = new Set(
        cacheResults.filter(([, cached]) => cached).map(([roomId]) => roomId)
      );
      setPreloadStates(Object.fromEntries(roomsToPreload.map((room) => [
        room.id,
        cachedRoomIds.has(room.id)
          ? { status: "complete", progress: 1 }
          : { status: "queued", progress: 0 },
      ])));

      for (const room of roomsToPreload) {
        if (controller.signal.aborted) return;
        if (cachedRoomIds.has(room.id)) continue;
        setPreloadStates((states) => ({
          ...states,
          [room.id]: { status: "loading", progress: 0 },
        }));
        try {
          await preloadUnityRoom(
            room.id,
            (progress) => setPreloadStates((states) => ({
              ...states,
              [room.id]: { status: "loading", progress },
            })),
            controller.signal
          );
          markUnityRoomPreloaded(room.id);
          setPreloadStates((states) => ({
            ...states,
            [room.id]: { status: "complete", progress: 1 },
          }));
        } catch (error) {
          if (error.name === "AbortError") return;
          console.warn(
            `Unable to preload ${room.id}. The space can still be opened normally.`,
            error
          );
          setPreloadStates((states) => ({
            ...states,
            [room.id]: { status: "error", progress: 0 },
          }));
        }
      }
    };

    void preloadInPriorityOrder();
    return () => controller.abort();
  }, [currentTherapyType, loading, selectedSpace, selectedSpaceLoaded]);

  const handleEnterClick = async (roomType, meetingID = null) => {
    const userId = PlutonicuserID || localStorage.getItem("PlutonicuserID");

    const isAIRoom = roomType.includes("-ai-") || roomType.includes("ai");
    if (isAIRoom && !userAccess.canAccessAI) {
      alert("You don't have access to AI companion rooms. Please upgrade your subscription.");
      return;
    }
    if (!isAIRoom && !userAccess.canAccessTherapy) {
      alert("You don't have access to therapy rooms. Please contact support.");
      return;
    }
    if (isAIRoom && isAILocked) {
  alert("This AI companion room is currently locked. Please contact your therapist.");
  return;
}


    // Handle test rooms
    if (
      roomType === "test-beach" ||
      roomType === "test-park" ||
      roomType === "test-forest" ||
      roomType === "test-campsite"
    ) {
      const testMeetingID = `${roomType}-test-meeting-id`;
      sessionStorage.setItem("meetingID", testMeetingID);
      sessionStorage.setItem("userId", userId);

      const realRoomType =
        roomType === "test-beach"
          ? "beach"
          : roomType === "test-park"
          ? "park"
          : roomType === "test-forest"
          ? "forest"
          : roomType === "test-campsite"
          ? "campsite"
          : "";

      // console.log(`[TestPage] Navigating to Unity build for ${realRoomType} room`);
      navigate(`/unity-room/${realRoomType}`);
      return;
    }

    if (roomType.includes("-ai-")) {
      const userId = PlutonicuserID || localStorage.getItem("PlutonicuserID");
      const aiMeetingID = `${roomType}-meeting-${userId}`;
      // ✅ Create AI companion meeting record in MeetingData table
      await createAICompanionMeeting(userId);
      sessionStorage.setItem("meetingID", aiMeetingID);
      sessionStorage.setItem("userId", userId);
      navigate(`/unity-room/${roomType}`);
      return;
    }

    if (meetingID) {
      // Directly enter the selected session
      sessionStorage.setItem("meetingID", meetingID);
      sessionStorage.setItem("userId", userId);
      navigate(`/unity-room/${roomType}`);
      return;
    }

    // Otherwise fallback to finding an available session
    const roomSessions = enabledRoom[roomType];
    if (!roomSessions || roomSessions.length === 0) {
      // alert("Room is not enabled. Please wait or check your eligibility.");
      return;
    }

    const session = roomSessions.find((s) => s.isAvailable);
    if (!session) {
      // alert("No active session is available at this time.");
      return;
    }

    sessionStorage.setItem("meetingID", session.meetingID);
    sessionStorage.setItem("userId", userId);

    if (session.isAISession || currentTherapyType === "ai") {
      sessionStorage.setItem("isAISession", "true");
    }

    navigate(`/unity-room/${roomType}`);
  };

  const handleCDAAgree = () => {
    const userId = PlutonicuserID || localStorage.getItem("PlutonicuserID");
    if (userId) {
      const cdaAgreedKey = `cda_agreed_${userId}`;
      localStorage.setItem(cdaAgreedKey, "true");
      setShowCDAModal(false);
    }
  };

  const handleCDAClose = () => {
    navigate("/therapy-rooms");
  };

  // if (loading) {
  //   return (
  //     <div
  //       style={{
  //         height: "100vh",
  //         display: "flex",
  //         justifyContent: "center",
  //         alignItems: "center",
  //       }}
  //     >
  //       <p>Loading...</p>
  //     </div>
  //   );
  // }

  return (
    <div>
      <Navbar
      // setCurrentNickname={(nickname) => {
      //   setCurrentNickname(nickname);
      //   localStorage.setItem("currentNickname", nickname);
      // }}
      // currentNickname={currentNickname}
      // handleLogout={handleLogout}
      // setCurrentFormType={setCurrentFormType}
      // showButtons={true}
      // PlutonicuserID={
      //   PlutonicuserID || localStorage.getItem("PlutonicuserID")
      // }
      />
      <div
        className="flex flex-col items-center min-h-screen pt-24 pb-16 px-12 mx-auto"
        style={{
          background: "linear-gradient(to bottom, #24004266 40%, #4FCBE6 100%)",
        }}
      >
        <div className="w-4/5 max-w-7xl">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6 mt-8">
            <button
              onClick={() => navigate("/")}
              className="hover:text-blue-600 transition-colors"
            >
              Home
            </button>
            <span>›</span>
            <span className="text-gray-800 font-medium">
              {currentTherapyType === "ai"
                ? "Your Mental Health Companion"
                : "Therapy Rooms"}
            </span>
          </nav>

          {currentTherapyType !== "ai" && (
            <div className="flex items-center gap-3 mb-8">
              <h2 className="text-3xl font-bold">Your Virtual Spaces</h2>
              <RefreshMeetingsButton onRefresh={refreshMeetings} refreshing={refreshing} />
            </div>
          )}
          {refreshError && <p role="alert" className="mb-4 text-red-700">{refreshError}</p>}
          <h2 className="text-xl font-bold mb-8">
            {currentTherapyType === "ai"
              ? "Choose a Space to Meet Your Mental Health Companion (BETA)"
              : "Select Your Preferred Session Space"}
          </h2>

          <div
  className={`flex ${
    selectedRooms.length === 1
      ? "flex-row justify-center items-center gap-12"
      : "flex-row"
  }`}
>
            {currentTherapyType === "ai" && (
              <div className="hidden md:block sticky top-24 self-start p-4 flex flex-col min-w-[280px]">
                <div className="mb-6">
                  <img
                    src={aiAssistantModel}
                    alt="AI Mental Health Companion Nova"
                    className="w-[320px] h-[452px] object-contain rounded-lg"
                  />
                </div>

                <div className="text-center ml-4">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 -ml-2">
                    Talk to Your Mental Health Companion Nova
                  </h3>
                </div>
              </div>
            )}
            <div
                className={`grid gap-8 ${
                selectedRooms.length === 1
                  ? "grid-cols-1 justify-items-center max-w-2xl mx-auto"
                  : "grid-cols-1 xl:grid-cols-2 w-full"
              }`}
              >
              {selectedRooms.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 max-w-md mx-auto">
                    <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                      Access Restricted
                    </h3>
                    <p className="text-yellow-700 mb-4">
                      {currentTherapyType === "ai" 
                        ? "You don't have access to AI companion rooms with your current subscription."
                        : "You don't have access to therapy rooms with your current subscription."
                      }
                    </p>
                    <button
                      onClick={() => navigate("/")}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      Go to Home
                    </button>
                  </div>
                </div>
              ) : (
                selectedRooms.map((room) => (
                <TherapyRoomCard
                  key={room.id}
                  room={room}
                  isSelected={selectedSpace === room.id}
                  onSelect={() => handleSpaceSelection(room.id)}
                  onEnterClick={(meetingID) =>
                    handleEnterClick(room.id, meetingID)
                  }
                  sessions={enabledRoom[room.id] || []}
                  selectedTherapyType={currentTherapyType}
                  aiLockLoading={isAIMH ? aiLockLoading : false}
                  isAILocked={isAIMH ? isAILocked : false}
                  preloadState={preloadStates[room.id]}
                />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <WarningBanner/>
      <Footer />

      <CDAModal
        isOpen={showCDAModal}
        onAgree={handleCDAAgree}
        onClose={handleCDAClose}
      />
      <SessionFeedbackModal
        isOpen={showSessionFeedback}
        onClose={() => setShowSessionFeedback(false)}
      />
    </div>
  );
}

export default ClientTherapyRooms;
